import React, { useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Text, Loader } from '@/ui'
import AlertIcon from '@/assets/images/icons/toastsAlerts/Alert icon.svg'
import InfoIcon from '@/assets/images/icons/toastsAlerts/Info Icon.svg'
import SuccessIcon from '@/assets/images/icons/toastsAlerts/Success icon.svg'
import WarningIcon from '@/assets/images/icons/toastsAlerts/Warning icon.svg'
import getMutationErrorMessage from '@/helpers/get-mutation-error-message'
import {
  StyledHeader,
  StyledMessage,
  MessageContainer,
  ActionMessageContainer,
  StyledToast,
  ToastColor,
  ToastIcon,
  GlobalActionStyles,
} from './Styles'
import ProgressBar from './ProgressBar'
import ActionProgressBar from './ActionProgressBar'

interface ToastHeaderProps {
  title?: string
  defaultTitle: string
}

interface ActionToastProps {
  message: ReactNode
  actionLabel: string
  callback: () => void
}

interface ToastType {
  [key: string]: ToastTypeProps
}
interface ToastTypeProps {
  title: string
  color: string
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
}
interface ToastProps {
  message?: ReactNode
  type: 'error' | 'warning' | 'info' | 'success' | 'action'
  title?: string
  currentType: ToastTypeProps
  children?: ReactNode
}

const toastTypes: ToastType = {
  error: {
    title: 'Error',
    color: '#FF6F61;',
    icon: AlertIcon,
  },
  warning: {
    title: 'Warning',
    color: '#FDDA7E;',
    icon: WarningIcon,
  },
  info: {
    title: 'Info',
    color: '#0A5B8B;',
    icon: InfoIcon,
  },
  success: {
    title: 'Success',
    color: '#00A86B;',
    icon: SuccessIcon,
  },
  action: {
    title: 'Action',
    color: '#00A86B;',
    icon: SuccessIcon,
  },
}

const ToastHeader = ({ title, defaultTitle }: ToastHeaderProps) => {
  const { t } = useTranslation()
  return <StyledHeader>{title ?? t(defaultTitle)}</StyledHeader>
}

const Toast = ({ message, type, title, currentType, children }: ToastProps) => {
  const [paused, setPaused] = useState(false)

  return (
    <StyledToast
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      key={type}>
      <ToastColor color={currentType.color} />
      <ToastIcon
        src={currentType.icon as any}
        alt={`${currentType.title} icon`}
      />
      <MessageContainer>
        <ToastHeader title={title} defaultTitle={currentType.title} />
        {!children ? <StyledMessage>{message}</StyledMessage> : children}
        <ProgressBar color={currentType.color} paused={paused} />
      </MessageContainer>
    </StyledToast>
  )
}

const renderToast = (
  type: 'error' | 'warning' | 'info' | 'success',
  message?: ReactNode,
  title?: string,
  children?: JSX.Element,
) => {
  const currentType = toastTypes[type]

  toast[type](
    <Toast
      type={type}
      message={message}
      title={title}
      currentType={currentType}>
      {children}
    </Toast>,
    {
      pauseOnFocusLoss: false,
      hideProgressBar: true,
    },
  )
}

const ActionToast = ({ message, actionLabel, callback }: ActionToastProps) => {
  const { t } = useTranslation()
  const [paused, setPaused] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    if (loading) {
      return
    }

    try {
      setLoading(true)
      await callback()
    } catch (e) {
      toast.error(getMutationErrorMessage(e as Error, t))
    } finally {
      toast.dismiss()
    }
  }

  return (
    <StyledToast
      key="action"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}>
      <ActionMessageContainer loading={loading}>
        {loading && <Loader active />}
        <Text.Span>{message}</Text.Span>
        <Text.Span onClick={handleClick}>{actionLabel}</Text.Span>
      </ActionMessageContainer>

      <ActionProgressBar paused={loading || paused} />

      <GlobalActionStyles />
    </StyledToast>
  )
}

const renderActionToast: (
  message: ReactNode,
  actionLabel: string,
  callback: () => void,
) => void = (message, actionLabel, callback) => {
  toast(
    <ActionToast
      message={message}
      actionLabel={actionLabel}
      callback={callback}
    />,
    {
      pauseOnFocusLoss: false,
      hideProgressBar: true,
      closeOnClick: false,
      draggable: false,
    },
  )
}

const toastMessage = {
  error: (message: ReactNode, title?: string, children?: JSX.Element) =>
    renderToast('error', message, title, children),
  warning: (message: ReactNode, title?: string, children?: JSX.Element) =>
    renderToast('warning', message, title, children),
  info: (message: ReactNode, title?: string, children?: JSX.Element) =>
    renderToast('info', message, title, children),
  success: (message: ReactNode, title?: string, children?: JSX.Element) =>
    renderToast('success', message, title, children),
  action: (message: ReactNode, actionLabel: string, callback: () => void) =>
    renderActionToast(message, actionLabel, callback),
}

export default toastMessage
